/** LED oyunları için kurumsal reklam yerleşimi çalışma alanı. */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/lib/api";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Select,
  TableWrap,
} from "@/components/ui";
import { copy, useI18n } from "@/lib/i18n";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/lib/toast";
import type { CatalogItem, SessionItem } from "@shared/index";
import "./LedOperations.css";

interface Creative {
  id: string;
  name: string;
  mediaUrl: string;
  mediaKind: string;
  status: string;
  reviewNote: string | null;
}
interface Placement {
  id: string;
  productId: string;
  name: string;
  kind: string;
  aspectRatio: string | null;
  supportsVideo: boolean;
}
interface Campaign {
  id: string;
  name: string;
  status: string;
  startsAt: string;
  endsAt: string;
  reviewNote: string | null;
  sessionIds: string[];
  rotationCount: number;
}

export default function LedOperations() {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const toast = useToast();
  const qc = useQueryClient();
  const [creativeOpen, setCreativeOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [creative, setCreative] = useState({
    name: "",
    mediaUrl: "",
    mediaKind: "image",
    targetUrl: "",
    sponsorName: "",
    rightsConfirmed: false,
  });
  const [form, setForm] = useState({
    name: "",
    productId: "",
    placementId: "",
    creativeIds: [] as string[],
    sessionIds: [] as string[],
    startsAt: "",
    endsAt: "",
    durationSeconds: 8,
    campaignConsentConfirmed: false,
  });
  const products = useQuery({
    queryKey: ["catalog", "led-ad-products"],
    queryFn: () =>
      api.get<{ items: CatalogItem[] }>("/catalog", { ownedOnly: true }),
  });
  const ledProducts = useMemo(
    () =>
      (products.data?.items ?? []).filter(
        (item) => item.category === "Leds Play" && item.entitled,
      ),
    [products.data],
  );
  useEffect(() => {
    if (!form.productId && ledProducts[0])
      setForm((value) => ({ ...value, productId: ledProducts[0]!.id }));
  }, [form.productId, ledProducts]);
  const creatives = useQuery({
    queryKey: ["account", "led", "creatives"],
    queryFn: () => api.get<{ items: Creative[] }>("/account/led/creatives"),
  });
  const campaigns = useQuery({
    queryKey: ["account", "led", "campaigns"],
    queryFn: () => api.get<{ items: Campaign[] }>("/account/led/campaigns"),
  });
  const placements = useQuery({
    queryKey: ["account", "led", "placements", form.productId],
    queryFn: () =>
      api.get<{ items: Placement[] }>("/account/led/placements", {
        productId: form.productId,
      }),
    enabled: Boolean(form.productId),
  });
  const sessions = useQuery({
    queryKey: ["sessions", "led-ad-campaign"],
    queryFn: () =>
      api.get<{ items: SessionItem[] }>("/sessions", { pageSize: 100 }),
  });
  const eligibleSessions = useMemo(
    () =>
      (sessions.data?.items ?? []).filter(
        (item) =>
          item.productId === form.productId &&
          ["scheduled", "running"].includes(item.status),
      ),
    [form.productId, sessions.data],
  );
  useEffect(() => {
    const first = placements.data?.items[0];
    if (
      first &&
      !placements.data?.items.some((item) => item.id === form.placementId)
    )
      setForm((value) => ({ ...value, placementId: first.id }));
  }, [form.placementId, placements.data]);
  const addCreative = useMutation({
    mutationFn: () =>
      api.post("/account/led/creatives", {
        ...creative,
        targetUrl: creative.targetUrl || null,
        sponsorName: creative.sponsorName || null,
      }),
    onSuccess: () => {
      setCreativeOpen(false);
      setCreative({
        name: "",
        mediaUrl: "",
        mediaKind: "image",
        targetUrl: "",
        sponsorName: "",
        rightsConfirmed: false,
      });
      void qc.invalidateQueries({ queryKey: ["account", "led", "creatives"] });
      toast.success(c("Reklam içeriği oluşturuldu.", "Ad creative created."));
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c("İçerik oluşturulamadı.", "Creative could not be created."),
        ),
      ),
  });
  const uploadCreativeMedia = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append("file", file);
      return api.post<{ url: string; bytes: number }>("/account/led/media", body);
    },
    onSuccess: (result) => {
      setCreative((value) => ({ ...value, mediaUrl: result.url, mediaKind: "image" }));
      toast.success(c("Görsel güvenli medya alanına yüklendi.", "Image uploaded to secure media storage."));
    },
    onError: (error) => toast.error(message(error, c("Görsel yüklenemedi.", "Image could not be uploaded."))),
  });
  const createCampaign = useMutation({
    mutationFn: () =>
      api.post("/account/led/campaigns", {
        name: form.name,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        sessionIds: form.sessionIds,
        campaignConsentConfirmed: form.campaignConsentConfirmed,
        rotations: form.creativeIds.map((creativeId, index) => ({
          placementId: form.placementId,
          creativeId,
          sortOrder: index,
          durationSeconds: form.durationSeconds,
        })),
      }),
    onSuccess: () => {
      setCampaignOpen(false);
      setForm((current) => ({ ...current, name: "", creativeIds: [], sessionIds: [], campaignConsentConfirmed: false }));
      void qc.invalidateQueries({ queryKey: ["account", "led", "campaigns"] });
      toast.success(
        c("Kampanya taslağı oluşturuldu.", "Campaign draft created."),
      );
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c("Kampanya oluşturulamadı.", "Campaign could not be created."),
        ),
      ),
  });
  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/account/led/campaigns/${id}/submit`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["account", "led", "campaigns"] });
      toast.success(
        c("Kampanya incelemeye gönderildi.", "Campaign submitted for review."),
      );
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c("Kampanya gönderilemedi.", "Campaign could not be submitted."),
        ),
      ),
  });
  const approvedCreatives =
    creatives.data?.items.filter((item) => item.status === "approved") ?? [];

  return (
    <div className="customer-page led-ops stack stack--lg">
      <PageHeader
        title={c("LED reklam yerleşimleri", "LED ad placements")}
        description={c(
          "Oyun içi ve oyun dışı reklam alanlarını etkinlik zamanına göre planlayın; bir alanda birden fazla içeriği dönüşümlü oynatın.",
          "Schedule in-game and out-of-game placements by event time and rotate multiple creatives in one placement.",
        )}
        actions={
          <Button
            variant="primary"
            disabled={!ledProducts.length || !approvedCreatives.length}
            onClick={() => setCampaignOpen(true)}
          >
            {c("Kampanya oluştur", "Create campaign")}
          </Button>
        }
      />
      {!ledProducts.length && !products.isLoading ? (
        <EmptyState
          title={c("LED ürün yetkisi yok", "No LED product access")}
          description={c(
            "Reklam alanları yalnız kurumunuza tanımlı LED oyunlarında açılır.",
            "Ad placements become available only for LED games assigned to your organization.",
          )}
        />
      ) : null}
      <section className="led-ops__panel">
        <header>
          <div>
            <h2>{c("Reklam içerikleri", "Ad creatives")}</h2>
            <p>
              {c(
                "Onaylı Hub medyalarını kampanyalarda kullanın.",
                "Use approved Hub media in campaigns.",
              )}
            </p>
          </div>
          <Button onClick={() => setCreativeOpen(true)}>
            {c("İçerik ekle", "Add creative")}
          </Button>
        </header>
        {creatives.isLoading ? (
          <LoadingBlock rows={3} />
        ) : creatives.data?.items.length ? (
          <div className="led-creative-grid">
            {creatives.data.items.map((item) => (
              <article key={item.id}>
                <div className="led-creative-grid__media">
                  {item.mediaKind === "video" ? (
                    <video src={item.mediaUrl} muted />
                  ) : (
                    <img src={item.mediaUrl} alt="" />
                  )}
                </div>
                <div>
                  <strong>{item.name}</strong>
                  <Badge
                    tone={
                      item.status === "approved"
                        ? "success"
                        : item.status === "rejected"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {statusLabel(item.status, language)}
                  </Badge>
                  {item.reviewNote ? <small>{item.reviewNote}</small> : null}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            title={c("Reklam içeriği yok", "No ad creatives")}
            description={c(
              "İlk görsel veya videonuzu ekleyin; yayın öncesi Wondeon onayından geçer.",
              "Add your first image or video; it is reviewed by Wondeon before publishing.",
            )}
          />
        )}
      </section>
      <section className="led-ops__panel">
        <header>
          <div>
            <h2>{c("Kampanyalar", "Campaigns")}</h2>
            <p>
              {c(
                "Taslak, inceleme ve yayın durumunu tek akışta takip edin.",
                "Track draft, review, and publishing states in one flow.",
              )}
            </p>
          </div>
        </header>
        {campaigns.isLoading ? (
          <LoadingBlock rows={4} />
        ) : campaigns.data?.items.length ? (
          <TableWrap caption={c("LED reklam kampanyaları", "LED ad campaigns")}>
            <thead>
              <tr>
                <th>{c("Kampanya", "Campaign")}</th>
                <th>{c("Tarih", "Dates")}</th>
                <th>{c("Durum", "Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.data.items.map((item) => (
                <tr key={item.id}>
                  <td data-label={c("Kampanya", "Campaign")}>
                    <span className="led-campaign-summary">
                      <strong>{item.name}</strong>
                      <small className="muted">
                        {item.sessionIds.length
                          ? c(`${item.sessionIds.length} bağlı etkinlik`, `${item.sessionIds.length} linked event${item.sessionIds.length === 1 ? "" : "s"}`)
                          : c("Bağımsız zamanlama", "Independent schedule")}
                        {` · ${item.rotationCount} ${c("dönüşümlü içerik", "rotating creatives")}`}
                      </small>
                      {item.reviewNote ? (
                        <small className="muted">{item.reviewNote}</small>
                      ) : null}
                    </span>
                  </td>
                  <td data-label={c("Tarih", "Dates")}>
                    <span className="led-campaign-dates">
                      <time dateTime={item.startsAt}>{formatDateTime(item.startsAt)}</time>
                      <time dateTime={item.endsAt}>{formatDateTime(item.endsAt)}</time>
                    </span>
                  </td>
                  <td data-label={c("Durum", "Status")}>
                    <Badge
                      tone={
                        item.status === "approved" || item.status === "active"
                          ? "success"
                          : item.status === "rejected"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {statusLabel(item.status, language)}
                    </Badge>
                  </td>
                  <td data-label={c("İşlem", "Action")}>
                    {item.status === "draft" ? (
                      <Button
                        size="sm"
                        loading={submit.isPending}
                        onClick={() => submit.mutate(item.id)}
                      >
                        {c("İncelemeye gönder", "Submit for review")}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        ) : (
          <EmptyState
            title={c("Kampanya yok", "No campaigns")}
            description={c(
              "Onaylı içerikleri ürün reklam alanlarına bağlayarak başlayın.",
              "Start by assigning approved creatives to product ad placements.",
            )}
          />
        )}
      </section>
      <Modal
        open={creativeOpen}
        onClose={() => setCreativeOpen(false)}
        title={c("Reklam içeriği ekle", "Add ad creative")}
        description={c(
          "Kurumsal reklam görselini yükleyin; içerik yayınlanmadan önce Wondeon onayından geçer.",
          "Upload the institutional ad image; Wondeon reviews it before publishing.",
        )}
        footer={
          <>
            <Button onClick={() => setCreativeOpen(false)}>
              {c("Vazgeç", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={addCreative.isPending}
              disabled={
                creative.name.trim().length < 2 ||
                !creative.mediaUrl.startsWith("/medya/")
                || !creative.rightsConfirmed
              }
              onClick={() => addCreative.mutate()}
            >
              {c("İçeriği ekle", "Add creative")}
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field label={c("İçerik adı", "Creative name")} required>
            {({ id }) => (
              <Input
                id={id}
                value={creative.name}
                onChange={(event) =>
                  setCreative({ ...creative, name: event.target.value })
                }
              />
            )}
          </Field>
          <Field
            label={c("Reklam görseli", "Ad image")}
            hint={c("JPEG, PNG, WebP veya AVIF · en fazla 8 MB", "JPEG, PNG, WebP or AVIF · up to 8 MB")}
            required
          >
            {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={uploadCreativeMedia.isPending} onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadCreativeMedia.mutate(file);
            }} />}
          </Field>
          {uploadCreativeMedia.isPending ? <small className="muted">{c("Görsel yükleniyor…", "Uploading image…")}</small> : null}
          {creative.mediaUrl ? <div className="led-upload-preview"><img src={creative.mediaUrl} alt="" /><span>{c("Yükleme tamamlandı", "Upload complete")}</span></div> : null}
          <Field label={c("Hedef bağlantı", "Target URL")}>
            {({ id }) => <Input id={id} type="url" value={creative.targetUrl} onChange={(event) => setCreative({ ...creative, targetUrl: event.target.value })} />}
          </Field>
          <Field label={c("Sponsor / marka adı", "Sponsor / brand name")}>
            {({ id }) => <Input id={id} value={creative.sponsorName} onChange={(event) => setCreative({ ...creative, sponsorName: event.target.value })} />}
          </Field>
          <label className="led-rights-consent"><input type="checkbox" checked={creative.rightsConfirmed} onChange={(event) => setCreative({ ...creative, rightsConfirmed: event.target.checked })} /><span><strong>{c('Yayın hakkını onaylıyorum', 'I confirm publishing rights')}</strong><small>{c('Bu görseli, markayı ve varsa sponsor materyalini belirtilen etkinliklerde kullanma yetkisine sahip olduğumu onaylıyorum. Onay sürümü ve hesabım denetim kaydına işlenecek.', 'I confirm that I am authorized to use this image, brand and any sponsor material in the specified events. The consent version and my account will be recorded in the audit trail.')}</small></span></label>
        </div>
      </Modal>
      <Modal
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        size="lg"
        title={c("LED reklam kampanyası", "LED ad campaign")}
        description={c(
          "Seçilen içerikler aynı reklam alanında sırayla gösterilir.",
          "Selected creatives rotate in the same ad placement.",
        )}
        footer={
          <>
            <Button onClick={() => setCampaignOpen(false)}>
              {c("Vazgeç", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={createCampaign.isPending}
              disabled={
                !form.name ||
                !form.startsAt ||
                !form.endsAt ||
                !form.placementId ||
                !form.creativeIds.length ||
                !form.campaignConsentConfirmed
              }
              onClick={() => createCampaign.mutate()}
            >
              {c("Taslak oluştur", "Create draft")}
            </Button>
          </>
        }
      >
        <div className="admin-form-grid admin-form-grid--2">
          <Field label={c("Kampanya adı", "Campaign name")} required>
            {({ id }) => <Input id={id} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />}
          </Field>
          <Field label={c("LED oyunu", "LED game")} required>
            {({ id }) => <Select id={id}
              value={form.productId}
              onChange={(event) =>
                setForm({
                  ...form,
                  productId: event.target.value,
                  placementId: "",
                  sessionIds: [],
                })
              }
            >
              {ledProducts.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>}
          </Field>
          <Field label={c("Reklam alanı", "Ad placement")} required>
            {({ id }) => <Select id={id}
              value={form.placementId}
              onChange={(event) =>
                setForm({ ...form, placementId: event.target.value })
              }
            >
              {placements.data?.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ·{" "}
                  {item.kind === "in_game"
                    ? c("oyun içi", "in-game")
                    : c("oyun dışı", "out-of-game")}
                </option>
              ))}
            </Select>}
          </Field>
          <Field
            label={c("Her içerik süresi", "Duration per creative")}
            hint={c("Saniye", "Seconds")}
          >
            {({ id }) => <Input id={id}
              type="number"
              min={1}
              max={3600}
              value={form.durationSeconds}
              onChange={(event) =>
                setForm({
                  ...form,
                  durationSeconds: Number(event.target.value),
                })
              }
            />}
          </Field>
          <Field label={c("Başlangıç", "Start")} required>
            {({ id }) => <Input id={id}
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) =>
                setForm({ ...form, startsAt: event.target.value })
              }
            />}
          </Field>
          <Field label={c("Bitiş", "End")} required>
            {({ id }) => <Input id={id}
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) =>
                setForm({ ...form, endsAt: event.target.value })
              }
            />}
          </Field>
        </div>
        <fieldset className="led-session-picker">
          <legend>{c("Bağlı etkinlikler", "Linked events")}</legend>
          <p>
            {c(
              "Kampanyayı bir veya daha fazla planlanmış LED oturumuna bağlayın. Seçim tarih aralığını etkinliğe göre hazırlar; gerekirse sonradan daraltabilirsiniz.",
              "Link the campaign to one or more scheduled LED sessions. The selection prepares the date range from the event; you can narrow it afterward.",
            )}
          </p>
          {sessions.isLoading ? (
            <LoadingBlock rows={2} />
          ) : eligibleSessions.length ? (
            <div>
              {eligibleSessions.map((item) => (
                <label key={item.id}>
                  <input
                    type="checkbox"
                    checked={form.sessionIds.includes(item.id)}
                    onChange={(event) => {
                      const sessionIds = event.target.checked
                        ? [...form.sessionIds, item.id]
                        : form.sessionIds.filter((id) => id !== item.id);
                      const selected = eligibleSessions.filter((session) =>
                        sessionIds.includes(session.id),
                      );
                      const starts = selected.map((session) =>
                        new Date(session.startsAt).getTime(),
                      );
                      const ends = selected.map(
                        (session) =>
                          new Date(session.startsAt).getTime() +
                          session.durationMinutes * 60_000,
                      );
                      setForm({
                        ...form,
                        sessionIds,
                        startsAt: selected.length
                          ? toLocalInput(new Date(Math.min(...starts)))
                          : form.startsAt,
                        endsAt: selected.length
                          ? toLocalInput(new Date(Math.max(...ends)))
                          : form.endsAt,
                      });
                    }}
                  />
                  <span>
                    <strong>{item.productName}</strong>
                    <small>
                      {item.endpointName} · {formatDateTime(item.startsAt)} ·{" "}
                      {item.durationMinutes} {c("dk", "min")}
                    </small>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="led-session-picker__empty">
              <strong>{c("Planlanmış LED etkinliği yok", "No scheduled LED event")}</strong>
              <span>
                {c(
                  "Kampanyayı bağımsız zaman aralığıyla oluşturabilir veya önce Takvim’den bir LED oturumu planlayabilirsiniz.",
                  "Create the campaign with an independent time range or schedule a LED session from Calendar first.",
                )}
              </span>
            </div>
          )}
        </fieldset>
        <fieldset className="led-creative-picker">
          <legend>{c("Dönüşümlü içerikler", "Rotating creatives")}</legend>
          {approvedCreatives.map((item) => (
            <label key={item.id}>
              <input
                type="checkbox"
                checked={form.creativeIds.includes(item.id)}
                onChange={(event) =>
                  setForm({
                    ...form,
                    creativeIds: event.target.checked
                      ? [...form.creativeIds, item.id]
                      : form.creativeIds.filter((id) => id !== item.id),
                  })
                }
              />
              <img src={item.mediaUrl} alt="" />
              <span>{item.name}</span>
            </label>
          ))}
        </fieldset>
        <label className="led-consent-row">
          <input type="checkbox" checked={form.campaignConsentConfirmed} onChange={(event) => setForm({ ...form, campaignConsentConfirmed: event.target.checked })} />
          <span>{c('Bu kampanyanın seçilen tarih, etkinlik, sponsor içeriği ve reklam alanlarıyla incelemeye gönderilmesini açıkça onaylıyorum.', 'I explicitly confirm that this campaign may be submitted for review with the selected dates, events, sponsor content and placements.')}</span>
        </label>
      </Modal>
    </div>
  );
}

function statusLabel(value: string, language: "tr" | "en") {
  const tr: Record<string, string> = {
    draft: "Taslak",
    pending_review: "İncelemede",
    approved: "Onaylı",
    active: "Yayında",
    paused: "Duraklatıldı",
    rejected: "Düzeltme gerekli",
    completed: "Tamamlandı",
  };
  const en: Record<string, string> = {
    draft: "Draft",
    pending_review: "In review",
    approved: "Approved",
    active: "Live",
    paused: "Paused",
    rejected: "Changes required",
    completed: "Completed",
  };
  return (language === "tr" ? tr : en)[value] ?? value;
}
function message(error: unknown, fallback: string) {
  return error instanceof ApiRequestError ? error.message : fallback;
}

function toLocalInput(value: Date): string {
  const offset = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
}
